import React, { useEffect, useState } from 'react';
import { 
  BarChart3, 
  Users, 
  FolderOpen, 
  AlertTriangle, 
  CheckCircle, 
  Clock,
  TrendingUp,
  FileText,
  Calendar,
  Banknote,
  ArrowRight
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout/Layout';
import { supabase } from '../lib/supabase';

interface DashboardStats {
  total_projects: number;
  projects_by_status: Record<string, number>;
  projects_by_domain: Record<string, number>;
  document_completion: {
    total_projects: number;
    fully_completed: number;
    partially_completed: number;
    not_started: number;
  };
  funding_stats: {
    total_requested: number;
    total_approved: number;
    average_request: number;
    projects_with_funding: number;
  };
  recent_activity: Array<{
    project_id: string;
    project_name: string;
    status: string;
    updated_at: string;
    owner_name: string;
  }>;
  alerts: {
    overdue_projects: number;
    pending_documents: number;
    unassigned_projects: number;
  };
}

interface AdvisorStats {
  advisor_id: string;
  advisor_name: string;
  advisor_email: string;
  organization: string;
  active_projects: number;
  total_projects: number;
  avg_completion_rate: number;
}

export const CoordinatorDashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [advisorStats, setAdvisorStats] = useState<AdvisorStats[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setIsLoading(true);
      
      // Appeler les vraies fonctions RPC (plus de données mockées)
      const { data: dashboardData, error: dashboardError } = await supabase
        .rpc('get_coordinator_dashboard_stats');
      
      const { data: advisorData, error: advisorError } = await supabase
        .rpc('get_advisor_workload_stats');
      
      if (dashboardError) {
        console.error('Error loading dashboard stats:', dashboardError);
        // Afficher un message d'erreur spécifique
        console.warn('Fonction RPC get_coordinator_dashboard_stats non trouvée. Vérifiez que la migration a été appliquée.');
      }
      
      if (advisorError) {
        console.error('Error loading advisor stats:', advisorError);
        console.warn('Fonction RPC get_advisor_workload_stats non trouvée. Vérifiez que la migration a été appliquée.');
      }
      
      setStats(dashboardData);
      setAdvisorStats(advisorData || []);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
      // En cas d'erreur, ne pas utiliser de données mockées
      setStats(null);
      setAdvisorStats([]);
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusLabel = (status: string) => {
    const labels = {
      A_PREPARER: 'À préparer',
      SOUMIS: 'Soumis',
      ACCORD_PROVISOIRE: 'Accord provisoire',
      VALIDE: 'Validé',
      DEBLOCAGE_FONDS: 'Déblocage fonds',
      EN_COURS: 'En cours',
      VERIFICATION: 'Vérification',
      TERMINE: 'Terminé',
      REJETE: 'Rejeté',
      SUSPENDU: 'Suspendu'
    };
    return labels[status as keyof typeof labels] || status;
  };

  const getDomainLabel = (domain: string) => {
    const labels = {
      AGRICULTURE: 'Agriculture',
      ARTISANAT: 'Artisanat',
      COMMERCE: 'Commerce',
      SERVICES: 'Services',
      INDUSTRIE: 'Industrie',
      TOURISME: 'Tourisme',
      CULTURE: 'Culture',
      FORMATION: 'Formation',
      SOCIAL: 'Social',
      ENVIRONNEMENT: 'Environnement'
    };
    return labels[domain as keyof typeof labels] || domain;
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="p-6 flex items-center justify-center min-h-96">
          <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
        </div>
      </Layout>
    );
  }

  if (!stats) {
    return (
      <Layout>
        <div className="p-6">
          <div className="text-center py-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Erreur de chargement</h2>
            <p className="text-gray-600">Impossible de charger les données du tableau de bord.</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-indigo-600 rounded-2xl p-8 text-white">
          <h1 className="text-3xl font-bold mb-2">Vue d'ensemble CRESS</h1>
          <p className="text-purple-100 text-lg">
            Supervision et coordination des projets ESS en Guadeloupe
          </p>
        </div>

        {/* Alerts */}
        {(stats.alerts.overdue_projects > 0 || stats.alerts.pending_documents > 0 || stats.alerts.unassigned_projects > 0) && (
          <div className="bg-orange-50 border border-orange-200 rounded-xl p-6">
            <div className="flex items-center space-x-2 mb-4">
              <AlertTriangle className="w-5 h-5 text-orange-600" />
              <h2 className="text-lg font-semibold text-orange-900">Alertes prioritaires</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {stats.alerts.overdue_projects > 0 && (
                <div className="bg-white rounded-lg p-4 border border-orange-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Projets en retard</p>
                      <p className="text-2xl font-bold text-orange-600">{stats.alerts.overdue_projects}</p>
                    </div>
                    <Clock className="w-8 h-8 text-orange-400" />
                  </div>
                </div>
              )}
              {stats.alerts.pending_documents > 0 && (
                <div className="bg-white rounded-lg p-4 border border-orange-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Documents en attente</p>
                      <p className="text-2xl font-bold text-orange-600">{stats.alerts.pending_documents}</p>
                    </div>
                    <FileText className="w-8 h-8 text-orange-400" />
                  </div>
                </div>
              )}
              {stats.alerts.unassigned_projects > 0 && (
                <div className="bg-white rounded-lg p-4 border border-orange-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Projets non assignés</p>
                      <p className="text-2xl font-bold text-orange-600">{stats.alerts.unassigned_projects}</p>
                    </div>
                    <Users className="w-8 h-8 text-orange-400" />
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Main Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 mb-1">Total projets</p>
                <p className="text-3xl font-bold text-gray-900">{stats.total_projects}</p>
              </div>
              <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center">
                <FolderOpen className="w-6 h-6 text-white" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 mb-1">Financement total</p>
                <p className="text-3xl font-bold text-gray-900">
                  {(stats.funding_stats.total_approved / 1000000).toFixed(1)}M€
                </p>
                <p className="text-xs text-gray-500">
                  sur {(stats.funding_stats.total_requested / 1000000).toFixed(1)}M€ demandés
                </p>
              </div>
              <div className="w-12 h-12 bg-green-600 rounded-lg flex items-center justify-center">
                <Banknote className="w-6 h-6 text-white" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 mb-1">Docs complétés</p>
                <p className="text-3xl font-bold text-gray-900">
                  {Math.round((stats.document_completion.fully_completed / stats.document_completion.total_projects) * 100)}%
                </p>
                <p className="text-xs text-gray-500">
                  {stats.document_completion.fully_completed}/{stats.document_completion.total_projects} projets
                </p>
              </div>
              <div className="w-12 h-12 bg-purple-600 rounded-lg flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-white" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 mb-1">Accompagnateurs</p>
                <p className="text-3xl font-bold text-gray-900">{advisorStats.length}</p>
                <p className="text-xs text-gray-500">actifs</p>
              </div>
              <div className="w-12 h-12 bg-orange-600 rounded-lg flex items-center justify-center">
                <Users className="w-6 h-6 text-white" />
              </div>
            </div>
          </div>
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Projects by Status */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Répartition par statut</h3>
            <div className="space-y-3">
              {Object.entries(stats.projects_by_status).map(([status, count]) => (
                <div key={status} className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">{getStatusLabel(status)}</span>
                  <div className="flex items-center space-x-2">
                    <div className="w-20 bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-blue-600 h-2 rounded-full" 
                        style={{ width: `${(count / stats.total_projects) * 100}%` }}
                      />
                    </div>
                    <span className="text-sm font-medium text-gray-900 w-8">{count}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Projects by Domain */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Répartition par secteur</h3>
            <div className="space-y-3">
              {Object.entries(stats.projects_by_domain).map(([domain, count]) => (
                <div key={domain} className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">{getDomainLabel(domain)}</span>
                  <div className="flex items-center space-x-2">
                    <div className="w-20 bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-green-600 h-2 rounded-full" 
                        style={{ width: `${(count / stats.total_projects) * 100}%` }}
                      />
                    </div>
                    <span className="text-sm font-medium text-gray-900 w-8">{count}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Bottom Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Advisor Workload */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Charge de travail accompagnateurs</h3>
              <button
                onClick={() => navigate('/coordinator/advisors')}
                className="text-blue-600 hover:text-blue-700 text-sm font-medium flex items-center space-x-1"
              >
                <span>Gérer</span>
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
            <div className="space-y-3">
              {advisorStats.slice(0, 5).map((advisor) => (
                <div key={advisor.advisor_id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <p className="font-medium text-gray-900">{advisor.advisor_name}</p>
                    <p className="text-sm text-gray-600">{advisor.organization}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium text-gray-900">{advisor.active_projects} projets actifs</p>
                    <p className="text-xs text-gray-500">{Math.round(advisor.avg_completion_rate)}% progression moy.</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Recent Activity */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Activité récente</h3>
              <button
                onClick={() => navigate('/coordinator/projects')}
                className="text-blue-600 hover:text-blue-700 text-sm font-medium flex items-center space-x-1"
              >
                <span>Voir tous</span>
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
            <div className="space-y-3">
              {stats.recent_activity.slice(0, 5).map((activity) => (
                <div key={activity.project_id} className="flex items-center justify-between p-3 hover:bg-gray-50 rounded-lg cursor-pointer">
                  <div>
                    <p className="font-medium text-gray-900">{activity.project_name}</p>
                    <p className="text-sm text-gray-600">par {activity.owner_name}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium text-gray-900">{getStatusLabel(activity.status)}</p>
                    <p className="text-xs text-gray-500">
                      {new Date(activity.updated_at).toLocaleDateString('fr-FR')}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Actions rapides</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <button
              onClick={() => navigate('/coordinator/projects')}
              className="p-4 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors text-left"
            >
              <FolderOpen className="w-6 h-6 text-blue-600 mb-2" />
              <p className="font-medium text-gray-900">Gérer tous les projets</p>
              <p className="text-sm text-gray-600">Vue d'ensemble et actions</p>
            </button>
            
            <button
              onClick={() => navigate('/coordinator/documents')}
              className="p-4 bg-purple-50 hover:bg-purple-100 rounded-lg transition-colors text-left"
            >
              <FileText className="w-6 h-6 text-purple-600 mb-2" />
              <p className="font-medium text-gray-900">Vérifier documents</p>
              <p className="text-sm text-gray-600">Validation documentaire</p>
            </button>
            
            <button
              onClick={() => navigate('/admin/accompagnateurs')}
              className="p-4 bg-green-50 hover:bg-green-100 rounded-lg transition-colors text-left"
            >
              <Users className="w-6 h-6 text-green-600 mb-2" />
              <p className="font-medium text-gray-900">Accompagnateurs</p>
              <p className="text-sm text-gray-600">Affectation et suivi</p>
            </button>
            
            <button
              onClick={() => navigate('/coordinator/calendar')}
              className="p-4 bg-orange-50 hover:bg-orange-100 rounded-lg transition-colors text-left"
            >
              <Calendar className="w-6 h-6 text-orange-600 mb-2" />
              <p className="font-medium text-gray-900">Calendrier global</p>
              <p className="text-sm text-gray-600">Tous les rendez-vous</p>
            </button>
          </div>
        </div>
      </div>
    </Layout>
  );
};